import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { BASE_URL } from "utils/constant";
import CreateDesignModal from "./CreateDesignModal";
import RequireAccess from "components/RequireAccess";
import { Download, Trash2, Search, Plus, Eye, FileImage } from "lucide-react";
import DrawingDetailView from "./DesignDetail";
import {
  getStatusBadgeClass,
  downloadDiagram,
  getDesignsByOrg,
  deleteDesignById,
  getDesignApsStatus,
} from "../../../api/design/service";
import { useAlert } from "components/UI_components/Alert";
import { APSViewer } from "./APSViewer";
import LoadingScreen from "components/UI_components/LoadingScreen";

function extractApsProgressPercent(res) {
  const raw =
    res?.aps_progress ??
    res?.aps_percentage ??
    res?.aps_percent ??
    res?.progress ??
    res?.percentage ??
    res?.percent ??
    res?.data?.aps_progress ??
    res?.data?.progress ??
    res?.data?.percentage ??
    res?.data?.percent ??
    null;

  if (raw === null || typeof raw === "undefined") return null;

  const n =
    typeof raw === "number"
      ? raw
      : typeof raw === "string"
        ? Number(raw.replace("%", "").trim())
        : NaN;

  if (!Number.isFinite(n)) return null;
  return Math.max(0, Math.min(100, Math.round(n)));
}

export default function DesignManagement({ currentMember, permissionsLoaded }) {
  const { addAlert } = useAlert();
  const [selectedDrawing, setSelectedDrawing] = useState(null);
  const [drawings, setDrawings] = useState(
    JSON.parse(localStorage.getItem("drawings")) || [],
  );
  const [loadingDesigns, setLoadingDesigns] = useState(false);
  const [designsError, setDesignsError] = useState(null);
  const drawingsRef = useRef(drawings);
  const apsPollInFlightRef = useRef(false);
  const hasDesignAccess =
    currentMember?.joined_as === "owner" ||
    currentMember?.is_admin === true ||
    currentMember?.design_management === true ||
    currentMember?.design_admin === true;

  useEffect(() => {
    drawingsRef.current = drawings;
  }, [drawings]);

  const patchDrawingById = useCallback((drawingId, patch) => {
    if (!drawingId) return;

    setDrawings((prev) =>
      prev.map((d) => (d?.id === drawingId ? { ...d, ...(patch || {}) } : d)),
    );
    setSelectedDrawing((prev) =>
      prev?.id === drawingId ? { ...prev, ...(patch || {}) } : prev,
    );
  }, []);

  const fetchDesigns = async () => {
    try {
      if (drawings.length === 0) {
        setLoadingDesigns(true);
      }
      const org = JSON.parse(localStorage.getItem("organization") || "{}");
      const orgId = org?.id;
      if (!orgId) {
        setDrawings([]);
        setLoadingDesigns(false);
        return;
      }

      const mapped = await getDesignsByOrg(orgId);
      setDrawings(mapped);
      localStorage.setItem("drawings", JSON.stringify(mapped));
    } catch (err) {
      console.error("Failed to load designs", err);
      setDesignsError(err);
      setDrawings([]);
    } finally {
      setLoadingDesigns(false);
    }
  };

  useEffect(() => {
    if (!hasDesignAccess) return;
    fetchDesigns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasDesignAccess]);

  const hasProcessingDwgs = useMemo(() => {
    return drawings.some((d) => {
      const isDwg = d?.file_type?.toLowerCase() === "dwg";
      const status = (d?.aps_status || "").toLowerCase();
      return isDwg && status && status !== "success" && status !== "failed";
    });
  }, [drawings]);

  // Poll APS processing status/progress for DWG files still processing
  useEffect(() => {
    if (!hasProcessingDwgs) return;

    let cancelled = false;
    const interval = setInterval(async () => {
      if (cancelled) return;
      if (apsPollInFlightRef.current) return;

      const candidates = (drawingsRef.current || []).filter((d) => {
        const isDwg = d?.file_type?.toLowerCase() === "dwg";
        const status = (d?.aps_status || "").toLowerCase();
        return isDwg && status && status !== "success" && status !== "failed";
      });

      if (candidates.length === 0) return;

      apsPollInFlightRef.current = true;
      try {
        const results = await Promise.allSettled(
          candidates.map(async (d) => {
            const res = await getDesignApsStatus(d.id);
            return { id: d.id, res };
          }),
        );

        if (cancelled) return;

        results.forEach((r) => {
          if (r.status !== "fulfilled") return;
          const { id, res } = r.value || {};
          if (!id || !res) return;

          const patch = {
            aps_status: res?.aps_status,
            aps_progress: extractApsProgressPercent(res),
            urn: res?.urn ?? res?.aps_urn,
          };

          patchDrawingById(id, patch);
        });
      } finally {
        apsPollInFlightRef.current = false;
      }
    }, 8000);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [hasProcessingDwgs, patchDrawingById]);

  if (!hasDesignAccess && permissionsLoaded) {
    return (
      <RequireAccess
        module="Design"
        currentMember={currentMember}
        permission="design_management"
      />
    );
  }

  const handleBack = () => setSelectedDrawing(null);

  const handleSelectDrawing = async (drawing) => {
    if (drawing.file_type?.toLowerCase() === "dwg") {
      if (drawing.aps_status !== "success") {
        try {
          const res = await getDesignApsStatus(drawing.id);
          const apsProgress = extractApsProgressPercent(res);
          patchDrawingById(drawing.id, {
            aps_status: res?.aps_status,
            aps_progress: apsProgress,
            urn: res?.urn ?? res?.aps_urn,
          });
          if (res.aps_status !== "success") {
            const statusMsg =
              res.aps_status === "failed"
                ? "DWG file processing failed."
                : "DWG file is still being processed. Please wait.";
            addAlert(statusMsg, "warning");
            return;
          }
          // Update the drawing with new status so it can be opened
          drawing = {
            ...drawing,
            aps_status: res.aps_status,
            aps_progress: apsProgress,
            urn: res?.urn ?? res?.aps_urn ?? drawing?.urn,
          };
        } catch (err) {
          console.error("Failed to check APS status", err);
          addAlert("Failed to check file status. Please try again.", "error");
          return;
        }
      }
    }
    setSelectedDrawing(drawing);
  };

  // Supports either a full drawing object OR a functional updater (prev => next).
  // This prevents async updates (pins/comments/versions) from clobbering each other.
  const handleUpdateDrawing = (update) => {
    setSelectedDrawing((prevSelected) => {
      const nextSelected =
        typeof update === "function" ? update(prevSelected) : update;
      const targetId = nextSelected?.id ?? prevSelected?.id;

      if (targetId) {
        setDrawings((prev) =>
          prev.map((d) =>
            d?.id === targetId ? { ...d, ...(nextSelected || {}) } : d,
          ),
        );
      }

      return nextSelected;
    });
  };

  const handleCreateDesign = (design) => {
    // This function is now only used for optimistic updates if needed,
    // but we will rely on refetching for accuracy.
    // However, to keep the prop signature for DesignDashboard, we keep it.
    // But DesignDashboard will now call onRefresh instead of just onCreateDesign
  };

  const handleDeleteDesign = (id) =>
    setDrawings((prev) => prev.filter((d) => d?.id !== id));

  if (selectedDrawing) {
    if (
      (selectedDrawing.file_type === "dwg" ||
        selectedDrawing.file_type === "DWG") &&
      selectedDrawing.urn
    ) {
      return (
        <APSViewer
          drawing={selectedDrawing}
          onBack={handleBack}
          onUpdate={handleUpdateDrawing}
          onDelete={handleDeleteDesign}
          currentMember={currentMember}
        />
      );
    }
    return (
      <DrawingDetailView
        drawing={selectedDrawing}
        onBack={handleBack}
        onUpdate={handleUpdateDrawing}
        onDelete={handleDeleteDesign}
        currentMember={currentMember}
      />
    );
  }

  return (
    <DesignDashboard
      drawings={drawings}
      onSelectDrawing={handleSelectDrawing}
      onCreateDesign={handleCreateDesign}
      onDeleteDesign={handleDeleteDesign}
      onRefresh={fetchDesigns}
      loadingDesigns={loadingDesigns}
    />
  );
}

function DesignDashboard({
  drawings,
  onSelectDrawing,
  onCreateDesign,
  onDeleteDesign,
  onRefresh,
  loadingDesigns,
}) {
  const { addAlert } = useAlert();
  const [searchQuery, setSearchQuery] = useState("");
  const [projectFilter, setProjectFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [assigneeFilter, setAssigneeFilter] = useState("all");

  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const [formData, setFormData] = useState({
    name: "",
    projectName: "",
    assignee: "",
    fileType: "",
  });

  const projects = Array.from(new Set(drawings.map((d) => d.projectName)));
  const designTypes = [
    "Layout",
    "Electrical",
    "HVAC",
    "Plumbing",
    "Elevation",
    "Structural",
  ];

  const assignees = Array.from(
    new Map(
      drawings
        .flatMap((d) => d.assignee)
        .map((member) => [member?.user?.id, member?.user]),
    ).values(),
  );
  const filteredDrawings = drawings.filter((drawing) => {
    const matchesSearch =
      drawing.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      drawing.projectName.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesProject =
      projectFilter === "all" || drawing.projectName === projectFilter;
    const matchesStatus =
      statusFilter === "all" || drawing.status === statusFilter;
    const matchesType = typeFilter === "all" || drawing.fileType === typeFilter;
    const matchesAssignee =
      assigneeFilter === "all" ||
      drawing.assignee.some(
        (member) =>
          member.user.first_name + " " + member.user.last_name ===
          assigneeFilter,
      );
    return (
      matchesSearch &&
      matchesProject &&
      matchesStatus &&
      matchesType &&
      matchesAssignee
    );
  });

  // const stats = {
  //   total: drawings.length,
  //   approved: drawings.filter(d => d.status === "Approved").length,
  //   pending: drawings.filter(d => d.status === "Pending" || d.status === "Under Review").length,
  //   locked: drawings.filter(d => d.status === "Locked").length,
  // };
  // AFTER
  const stats = {
    total: drawings.length,
    approved: drawings.filter((d) => d.status === "Approved").length,
    pending: drawings.filter(
      (d) => d.status === "Pending" || d.status === "Under Review",
    ).length,
  };
  const handleSubmitCreate = async (
    createdDesignResponse,
    originalFormData = null,
    selectedSite = null,
  ) => {
    // If called with createdDesign from the server, use that to add to UI
    if (createdDesignResponse) {
      // Instead of manually constructing the object and guessing the ID,
      // we simply refresh the list from the server.
      // This ensures we get the correct ID and data format.
      if (onRefresh) {
        await onRefresh();
      }

      setShowCreateDialog(false);
      return;
    }

    // Fallback: local creation if no server response provided
    if (
      !formData.name ||
      !formData.projectName ||
      !formData.assignee ||
      !formData.fileType
    ) {
      return;
    }

    onCreateDesign({
      name: formData.name,
      projectName: formData.projectName,
      assignee: formData.assignee,
      fileType: formData.fileType,
      status: "Pending",
      lastUpdated: new Date().toISOString().split("T")[0],
      approverName: "Pending",
      uploadedBy: formData.assignee,
      linkedTask: undefined,
      linkedTasks: [],
    });

    setFormData({ name: "", projectName: "", assignee: "", fileType: "" });
    setShowCreateDialog(false);
    addAlert("Design created successfully", "success");
  };

  const handleConfirmDelete = () => {
    (async () => {
      if (!deleteId) return;
      try {
        await deleteDesignById(deleteId);
        onDeleteDesign(deleteId);
        addAlert("Design deleted successfully", "success");
      } catch (err) {
        console.error("Delete design failed", err);
        addAlert(err.message || "Failed to delete design", "error");
      } finally {
        setDeleteId(null);
      }
    })();
  };

  return (
    <div className="flex-1 w-full h-[91vh] overflow-y-scroll">
      <LoadingScreen loading={loadingDesigns}>
        <div className="w-full px-10 py-8 flex flex-col gap-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">
                Design Management
              </h1>
              <p className="text-sm text-gray-500 mt-1">
                Manage all project drawings and design files
              </p>
            </div>
            <button
              className="px-4 py-2 bg-purple-700 hover:bg-purple-800 text-white rounded-lg flex items-center gap-2 shadow-sm"
              onClick={() => setShowCreateDialog(true)}
            >
              <Plus className="w-4 h-4" /> Create Design
            </button>
          </div>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {[
              {
                label: "Total Designs",
                value: stats.total,
                icon: FileImage,
                bgColor: "bg-sm-secondary/10",
                iconColor: "text-sm-secondary",
              },
              {
                label: "Approved",
                value: stats.approved,
                icon: FileImage,
                bgColor: "bg-success/10",
                iconColor: "text-success",
              },
              {
                label: "Pending",
                value: stats.pending,
                icon: FileImage,
                bgColor: "bg-sm-accent/10",
                iconColor: "text-sm-accent",
              },
              // { label: "Locked", value: stats.locked, icon: FileImage, bgColor: "bg-sm-primary/10", iconColor: "text-sm-primary" },
            ].map((stat) => (
              <div
                key={stat.label}
                className="p-4 border rounded-xl bg-white shadow-sm"
              >
                <div className="flex items-center gap-3">
                  <div
                    className={`h-10 w-10 rounded-lg ${stat.bgColor} flex items-center justify-center border`}
                  >
                    <stat.icon className={`h-5 w-5 ${stat.iconColor}`} />
                  </div>
                  <div>
                    <p className="text-2xl font-semibold">{stat.value}</p>
                    <p className="text-xs text-gray-500">{stat.label}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative flex-1 min-w-[220px] max-w-md">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                placeholder="Search designs..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 h-9 text-sm border rounded w-full bg-white"
              />
            </div>
            <select
              value={projectFilter}
              onChange={(e) => setProjectFilter(e.target.value)}
              className="h-9 border rounded bg-white px-2 text-sm"
            >
              <option value="all">All Projects</option>
              {projects.map((project) => (
                <option key={project} value={project}>
                  {project}
                </option>
              ))}
            </select>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="h-9 border rounded bg-white px-2 text-sm"
            >
              <option value="all">All Types</option>
              {designTypes.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="h-9 border rounded bg-white px-2 text-sm"
            >
              <option value="all">All Status</option>
              <option value="Open">Open</option>
              <option value="Pending">Pending</option>
              {/* <option value="Under Review">Under Review</option> */}
              <option value="Approved">Approved</option>
              {/* <option value="Locked">Locked</option> */}
            </select>
            <select
              value={assigneeFilter}
              onChange={(e) => setAssigneeFilter(e.target.value)}
              className="h-9 border rounded bg-white px-2 text-sm"
            >
              <option value="all">All Assignees</option>
              {assignees.map((assignee) => {
                // console.log("A ASSIGNEE", assignee);
                return (
                  <option
                    key={assignee?.id}
                    value={assignee?.first_name + " " + assignee?.last_name}
                  >
                    {assignee?.first_name + " " + assignee?.last_name}
                  </option>
                );
              })}
            </select>
          </div>

          <div className="border rounded-xl overflow-hidden flex-1 bg-white shadow-sm">
            <table className="w-full bg-white">
              <thead className="bg-gray-50">
                <tr className="text-left text-xs text-gray-600">
                  <th className="py-3 pl-6 pr-4 font-medium">Design Name</th>
                  <th className="py-3 px-4 font-medium">Project</th>
                  <th className="py-3 px-4 font-medium">Type</th>
                  <th className="py-3 px-4 font-medium">Assignee</th>
                  <th className="py-3 px-4 font-medium">Status</th>
                  <th className="py-3 px-4 font-medium">Last Updated</th>
                  <th className="py-3 pl-4 pr-6 font-medium text-center w-[140px]">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredDrawings.map((drawing) => (
                  <tr
                    key={drawing?.id}
                    className="border-t hover:bg-gray-50 cursor-pointer text-sm group"
                    onClick={() => onSelectDrawing(drawing)}
                  >
                    <td className="py-4 pl-6 pr-4 font-medium text-gray-900">
                      <div className="flex flex-col items-start">
                        <span className="font-medium">{drawing.name}</span>
                        {drawing.file_type?.toLowerCase() === "dwg" &&
                          drawing.aps_status !== "success" && (
                            <div className="mt-1 flex items-center gap-2">
                              <span
                                className={`text-xs px-2 py-0.5 rounded ${String(drawing.aps_status).toLowerCase() ===
                                  "failed"
                                  ? "bg-red-50 text-red-600"
                                  : "bg-yellow-50 text-yellow-600"
                                  }`}
                              >
                                {String(drawing.aps_status).toLowerCase() ===
                                  "failed"
                                  ? "failed"
                                  : "pending"}
                              </span>
                              {String(drawing.aps_status).toLowerCase() !==
                                "failed" &&
                                typeof drawing.aps_progress === "number" && (
                                  <span className="text-xs text-gray-500">
                                    {Math.max(
                                      0,
                                      Math.min(
                                        100,
                                        Math.round(drawing.aps_progress),
                                      ),
                                    )}
                                    %
                                  </span>
                                )}
                            </div>
                          )}
                      </div>
                    </td>
                    <td className="py-4 px-4 text-gray-600">
                      {drawing.projectName}
                    </td>
                    <td className="py-4 px-4 text-gray-600">
                      {drawing.fileType}
                    </td>
                    <td className="py-4 px-4 text-gray-600">
                      <div className="flex items-center -space-x-2">
                        {drawing?.assignee?.slice(0, 4).map((member) => (
                          <div
                            key={member?.id}
                            className="h-7 w-7 rounded-full border-2 border-white bg-gray-100 flex items-center justify-center overflow-hidden"
                            title={`${member?.user?.first_name} ${member?.user?.last_name}`}
                          >
                            {member?.user?.userpic ? (
                              <img
                                src={member.user.userpic}
                                alt=""
                                className="h-full w-full object-cover"
                              />
                            ) : (
                              <span className="text-[10px] font-medium text-gray-600 uppercase">
                                {member?.user?.first_name?.[0]}
                                {member?.user?.last_name?.[0]}
                              </span>
                            )}
                          </div>
                        ))}
                        {drawing?.assignee?.length > 4 && (
                          <div className="h-7 w-7 rounded-full border-2 border-white bg-gray-50 flex items-center justify-center text-xs font-medium text-gray-600">
                            +{drawing.assignee.length - 4}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <span
                        className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeClass(
                          drawing.status,
                        )}`}
                      >
                        {drawing.status}
                      </span>
                    </td>
                    <td className="py-4 px-4 text-gray-600">
                      {drawing.lastUpdated}
                    </td>
                    <td
                      className="py-2 pr-6 text-right whitespace-nowrap"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="hidden group-hover:flex items-center justify-end gap-1">
                        <button
                          className="p-2 text-gray-600 hover:bg-sm-accent hover:text-white rounded"
                          onClick={() => onSelectDrawing(drawing)}
                          aria-label="View"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          className="p-2 text-gray-600 hover:bg-sm-accent hover:text-white rounded"
                          onClick={(e) => {
                            e.stopPropagation();
                            downloadDiagram(drawing.diagram, drawing.name);
                          }}
                          aria-label="Download"
                        >
                          <Download className="w-4 h-4" />
                        </button>
                        <button
                          className="p-2 text-red-600 hover:bg-red-50 rounded"
                          onClick={() => setDeleteId(drawing?.id)}
                          aria-label="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {filteredDrawings.length === 0 && (
              <div className="flex items-center justify-center py-16">
                <div className="text-center">
                  <p className="text-sm font-medium">No designs found</p>
                  <p className="text-xs text-gray-500 mt-1">
                    Try adjusting your filters
                  </p>
                </div>
              </div>
            )}
          </div>

          <CreateDesignModal
            show={showCreateDialog}
            onClose={() => setShowCreateDialog(false)}
            formData={formData}
            setFormData={setFormData}
            designTypes={designTypes}
            onSubmit={handleSubmitCreate}
          />

          {deleteId && (
            <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
              <div className="bg-white p-4 rounded">
                <p>Are you sure you want to delete this design?</p>
                <div className="flex justify-end gap-2 mt-2">
                  <button
                    className="px-3 py-1 border rounded"
                    onClick={() => setDeleteId(null)}
                  >
                    Cancel
                  </button>
                  <button
                    className="px-3 py-1 bg-red-600 text-white rounded"
                    onClick={handleConfirmDelete}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </LoadingScreen>
    </div>
  );
}
